import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Divider,
  Chip,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  TableSortLabel,
  Autocomplete,
  Snackbar,
  Tooltip,
  LinearProgress
} from '@mui/material';
import {
  Image as ImageIcon,
  ImageNotSupported,
  TrendingUp,
  Inventory as InventoryIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  CloudUpload as UploadIcon,
  CloudSync as SyncS3Icon,
  CompareArrows as CompareArrowsIcon,
  HourglassEmpty as HourglassEmptyIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { fetchDashboardData } from '../store/dashboardSlice';
import { getItemsList, getBrands, getCategories, getSeasons, getFrameShapes, getItemModels, exportItemsList } from '../api/items';
import * as XLSX from 'xlsx';
import { startS3Scan, getS3ScanStatus } from '../api/sync';
import ItemUploadModal from '../components/ItemUploadModal';

const FILTER_SESSION_KEY = 'dashboard_filters';

const loadSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(FILTER_SESSION_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { instance } = useMsal();

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/login' });
  };
  const {
    overview,
    loading,
    error
  } = useSelector((state) => state.dashboard);

  const _saved = loadSavedFilters();

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState(null);
  const [page, setPage] = useState(_saved?.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(_saved?.rowsPerPage ?? 25);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState(_saved?.searchTerm ?? '');
  const [searchInput, setSearchInput] = useState(_saved?.searchTerm ?? '');

  // Independent column text filter inputs (all feed into searchTerm on Enter)
  const [colFilterItemCode, setColFilterItemCode] = useState('');
  const [colFilterItemName, setColFilterItemName] = useState('');
  const [colFilterColorCode, setColFilterColorCode] = useState('');
  const [colFilterCollection, setColFilterCollection] = useState('');

  const [brandFilter, setBrandFilter] = useState(_saved?.brandFilter ?? ['LINDA FARROW LUXE']);
  const [categoryFilter, setCategoryFilter] = useState(_saved?.categoryFilter ?? []);
  const [seasonFilter, setSeasonFilter] = useState(_saved?.seasonFilter ?? []);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [frameShapes, setFrameShapes] = useState([]);
  const [itemModels, setItemModels] = useState([]);

  const [sortBy, setSortBy] = useState(_saved?.sortBy ?? 'created_date');
  const [sortOrder, setSortOrder] = useState(_saved?.sortOrder ?? 'DESC');
  const [hasImageFilter, setHasImageFilter] = useState(_saved?.hasImageFilter ?? null);
  const [inStockFilter, setInStockFilter] = useState(_saved?.inStockFilter ?? null);
  const [hasPoFilter, setHasPoFilter] = useState(_saved?.hasPoFilter ?? null);
  const [frameShapeFilter, setFrameShapeFilter] = useState(_saved?.frameShapeFilter ?? []);
  const [aiFrameShapeFilter, setAiFrameShapeFilter] = useState(_saved?.aiFrameShapeFilter ?? []);
  const [itemModelFilter, setItemModelFilter] = useState(_saved?.itemModelFilter ?? []);
  const [noAiFrameShapeFilter, setNoAiFrameShapeFilter] = useState(_saved?.noAiFrameShapeFilter ?? false);
  const [frameShapeMismatchFilter, setFrameShapeMismatchFilter] = useState(_saved?.frameShapeMismatchFilter ?? false);
  const [frameShapeMatchFilter, setFrameShapeMatchFilter] = useState(_saved?.frameShapeMatchFilter ?? false);
  const [noPhotoGrOverdueFilter, setNoPhotoGrOverdueFilter] = useState(_saved?.noPhotoGrOverdueFilter ?? false);
  const [photoNoOrMismatchFilter, setPhotoNoOrMismatchFilter] = useState(_saved?.photoNoOrMismatchFilter ?? false);

  // Persist filters to sessionStorage whenever they change
  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_SESSION_KEY, JSON.stringify({
        page, rowsPerPage, searchTerm, brandFilter, categoryFilter,
        seasonFilter, sortBy, sortOrder, hasImageFilter, inStockFilter, hasPoFilter, frameShapeFilter, aiFrameShapeFilter, itemModelFilter,
        noAiFrameShapeFilter, frameShapeMismatchFilter, frameShapeMatchFilter, noPhotoGrOverdueFilter, photoNoOrMismatchFilter
      }));
    } catch { /* ignore */ }
  }, [page, rowsPerPage, searchTerm, brandFilter, categoryFilter, seasonFilter, sortBy, sortOrder, hasImageFilter, inStockFilter, hasPoFilter, frameShapeFilter, aiFrameShapeFilter, itemModelFilter, noAiFrameShapeFilter, frameShapeMismatchFilter, frameShapeMatchFilter, noPhotoGrOverdueFilter, photoNoOrMismatchFilter]);

  // S3 → DB scan state
  const [s3ScanStatus, setS3ScanStatus] = useState(null); // null | 'running' | 'completed' | 'failed'
  const [s3ScanResult, setS3ScanResult] = useState(null); // { added, updated, deleted, ... }
  const [s3ScanError, setS3ScanError] = useState(null);
  const [s3ScanSnackOpen, setS3ScanSnackOpen] = useState(false);
  const s3PollRef = useRef(null);
  const titleRowRef = useRef(null);
  const [titleRowHeight, setTitleRowHeight] = useState(0);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedItemForUpload, setSelectedItemForUpload] = useState(null);
  const [uploadSnackOpen, setUploadSnackOpen] = useState(false);
  const [uploadSnackMessage, setUploadSnackMessage] = useState('');

  // Excel export state
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchDashboardData({
      brands: brandFilter,
      categories: categoryFilter,
      seasons: seasonFilter,
      search: searchTerm
    }));
  }, [dispatch, brandFilter, categoryFilter, seasonFilter, searchTerm]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    if (titleRowRef.current) {
      setTitleRowHeight(titleRowRef.current.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [page, rowsPerPage, searchTerm, brandFilter, categoryFilter, seasonFilter, sortBy, sortOrder, hasImageFilter, inStockFilter, hasPoFilter, frameShapeFilter, aiFrameShapeFilter, itemModelFilter, noAiFrameShapeFilter, frameShapeMismatchFilter, frameShapeMatchFilter, noPhotoGrOverdueFilter, photoNoOrMismatchFilter]);

  const handleRefresh = async () => {
    dispatch(fetchDashboardData({
      brands: brandFilter,
      categories: categoryFilter,
      seasons: seasonFilter,
      search: searchTerm
    }));
    fetchItems();
  };

  // ── S3 → DB scan ─────────────────────────────────────────────────────────
  const stopS3Poll = () => {
    if (s3PollRef.current) {
      clearInterval(s3PollRef.current);
      s3PollRef.current = null;
    }
  };

  const handleS3Scan = async () => {
    if (s3ScanStatus === 'running') return;
    setS3ScanStatus('running');
    setS3ScanResult(null);
    setS3ScanError(null);
    try {
      await startS3Scan();
    } catch (err) {
      setS3ScanStatus('failed');
      setS3ScanError(err?.response?.data?.error || err.message || 'Failed to start scan');
      setS3ScanSnackOpen(true);
      return;
    }
    // Poll every 2 s until done
    stopS3Poll();
    s3PollRef.current = setInterval(async () => {
      try {
        const resp = await getS3ScanStatus();
        const data = resp?.data?.data || resp?.data || {};
        if (data.status === 'completed') {
          stopS3Poll();
          setS3ScanStatus('completed');
          setS3ScanResult(data);
          setS3ScanSnackOpen(true);
          // Auto-refresh dashboard data now that DB is updated
          handleRefresh();
        } else if (data.status === 'failed') {
          stopS3Poll();
          setS3ScanStatus('failed');
          setS3ScanError(data.error || 'Scan failed');
          setS3ScanSnackOpen(true);
        } else {
          // still running — update progress info
          setS3ScanResult(data);
        }
      } catch (pollErr) {
        console.error('S3 scan poll error:', pollErr);
      }
    }, 2000);
  };

  // Cleanup poll on unmount
  useEffect(() => () => stopS3Poll(), []);

  const fetchFilterOptions = async () => {
    try {
      const [brandsResult, categoriesResult, seasonsResult, frameShapesResult, itemModelsResult] = await Promise.all([
        getBrands(),
        getCategories(),
        getSeasons(),
        getFrameShapes(),
        getItemModels()
      ]);

      if (brandsResult.success) setBrands(brandsResult.data);
      if (categoriesResult.success) setCategories(categoriesResult.data);
      if (seasonsResult.success) {
        setSeasons(seasonsResult.data);
        // Only set the default season filter when there are no saved filters.
        // Setting it unconditionally would trigger an extra fetchItems call on
        // every mount because setSeasonFilter always produces a new array reference.
        if (!_saved?.seasonFilter?.length) {
          const lastYear = String(new Date().getFullYear() - 1);
          const defaultSeasons = seasonsResult.data
            .filter(s => String(s.vendor_season).includes(lastYear))
            .map(s => s.vendor_season);
          setSeasonFilter(defaultSeasons);
        }
      }
      if (frameShapesResult.success) setFrameShapes(frameShapesResult.data);
      if (itemModelsResult.success) setItemModels(itemModelsResult.data);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchItems = async () => {
    try {
      setItemsLoading(true);
      setItemsError(null);
      const offset = page * rowsPerPage;
      const result = await getItemsList(
        rowsPerPage,
        offset,
        searchTerm,
        brandFilter,
        categoryFilter,
        seasonFilter,
        sortBy,
        sortOrder,
        hasImageFilter,
        inStockFilter,
        hasPoFilter,
        frameShapeFilter,
        itemModelFilter,
        aiFrameShapeFilter,
        noAiFrameShapeFilter,
        frameShapeMismatchFilter,
        frameShapeMatchFilter,
        noPhotoGrOverdueFilter,
        photoNoOrMismatchFilter
      );

      if (result.success) {
        setItems(result.data);
        setTotalItems(result.pagination.total);
      } else {
        setItemsError(result.error || 'Failed to fetch items');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setItemsError(err.response?.data?.error || err.message || 'Failed to fetch items');
    } finally {
      setItemsLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(0);
  };

  const handleSearchKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(0);
  };

  const handleClearFilters = () => {
    setBrandFilter([]);
    setCategoryFilter([]);
    setSeasonFilter([]);
    setFrameShapeFilter([]);
    setAiFrameShapeFilter([]);
    setItemModelFilter([]);
    setNoAiFrameShapeFilter(false);
    setFrameShapeMismatchFilter(false);
    setFrameShapeMatchFilter(false);
    setNoPhotoGrOverdueFilter(false);
    setPhotoNoOrMismatchFilter(false);
    setSearchInput('');
    setSearchTerm('');
    setColFilterItemCode('');
    setColFilterItemName('');
    setColFilterColorCode('');
    setColFilterCollection('');
    setHasImageFilter(null);
    setInStockFilter(null);
    setHasPoFilter(null);
    setPage(0);
  };

  const hasActiveFilters = brandFilter.length > 0 || categoryFilter.length > 0 || seasonFilter.length > 0 || frameShapeFilter.length > 0 || aiFrameShapeFilter.length > 0 || itemModelFilter.length > 0 || noAiFrameShapeFilter || frameShapeMismatchFilter || frameShapeMatchFilter || noPhotoGrOverdueFilter || photoNoOrMismatchFilter || searchTerm || hasImageFilter !== null || inStockFilter !== null || hasPoFilter !== null;

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const data = await exportItemsList(
        searchTerm,
        brandFilter,
        categoryFilter,
        seasonFilter,
        sortBy,
        sortOrder,
        hasImageFilter,
        inStockFilter,
        hasPoFilter,
        frameShapeFilter,
        itemModelFilter,
        aiFrameShapeFilter,
        noAiFrameShapeFilter,
        frameShapeMismatchFilter,
        frameShapeMatchFilter,
        noPhotoGrOverdueFilter,
        photoNoOrMismatchFilter
      );

      const rows = (data || []).map(item => ({
        'Item Code': item.item_code || '',
        'V Item Code': item.v_item_code || '',
        'Item Name': item.item_name || '',
        'Brand': item.brand || '',
        'Brand Code': item.brand_code || '',
        'Category': item.category || '',
        'Collection': item.collection || '',
        'Season': item.vendor_season || '',
        'Image Count': item.image_count ?? 0,
        'Frame Shape': item.frame_shape || '',
        'AI Frame Shape': item.ai_frame_shape || '',
        'Color Code': item.color_code || '',
        'Color Detail': item.color_detail || '',
        'Color Front': item.color_front || '',
        'Color Temple': item.color_temple || '',
        'Color Lens': item.color_lens || '',
        'Item Model': item.item_model || '',
        'Created Date': item.created_date ? new Date(item.created_date).toLocaleDateString() : '',
        'HK Last BO Date': item.hk_last_bo_date ? new Date(item.hk_last_bo_date).toLocaleDateString() : '',
        'HK BO Qty': item.hk_bo_ord_qty ?? '',
        'CN Last BO Date': item.cn_last_bo_date ? new Date(item.cn_last_bo_date).toLocaleDateString() : '',
        'CN BO Qty': item.cn_bo_ord_qty ?? '',
        'SG Last BO Date': item.sg_last_bo_date ? new Date(item.sg_last_bo_date).toLocaleDateString() : '',
        'SG BO Qty': item.sg_bo_ord_qty ?? '',
        '1st GR Date': item.first_gr_date ? new Date(item.first_gr_date).toLocaleDateString() : '',
        'GR Total Qty': item.gr_total_qty ?? '',
        'Last Trx Date': item.last_trx_date ? new Date(item.last_trx_date).toLocaleDateString() : '',
        'Total Sales Qty': item.total_sales_item_qty ?? '',
        'Stock': item.stock ?? '',
        'Hold Qty': item.bo_hold_qty ?? '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Items');
      const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_').replace(':', '');
      XLSX.writeFile(wb, `pms_items_${timestamp}.xlsx`);
    } catch (err) {
      console.error('Export error:', err);
    } finally {
      setExportLoading(false);
    }
  };

  const handleViewItem = (itemCode) => {
    navigate(`/items/${itemCode}`);
  };

  const handleOpenUploadModal = (e, item) => {
    e.stopPropagation();
    setSelectedItemForUpload(item);
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
    setSelectedItemForUpload(null);
  };

  const handleUploadSuccess = (message) => {
    setUploadSnackMessage(message);
    setUploadSnackOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const percentOf = (value, total) => {
    const safeValue = value || 0;
    const safeTotal = total || 0;
    if (!safeTotal) return `${safeValue}/${safeTotal} = 0.0% of total items`;
    return `${safeValue}/${safeTotal} = ${((safeValue / safeTotal) * 100).toFixed(1)}% of total items`;
  };

  if (loading && !overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={handleRefresh}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Container>
    );
  }

  // Stats Card Component
  const StatsCard = ({ title, value, icon, color, subtitle, onClick, sx: sxProp }) => (
    <Paper
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      sx={{
        p: '3px 6px',
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        background: `linear-gradient(135deg, ${color === 'success' ? '#e8f5e9' : color === 'error' ? '#ffebee' : color === 'info' ? '#e3f2fd' : color === 'warning' ? '#fff8e1' : '#f5f5f5'} 0%, white 100%)`,
        border: `1px solid`,
        borderColor: `${color}.light`,
        transition: 'all 0.3s',
        '&:hover': {
          borderColor: `${color}.main`,
          boxShadow: 2
        },
        ...sxProp,
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography color="text.secondary" sx={{ lineHeight: 1, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography fontWeight="bold" color={`${color}.main`} sx={{ lineHeight: 1.1, fontSize: '0.85rem' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography color="text.secondary" sx={{ lineHeight: 1, display: 'block', fontSize: '0.55rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 18,
            height: 18,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );


  return (
    <Box sx={{ mt: 1, pb: 1.5, px: 2, height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
        <Box>
          <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
            PUYI Optical - PMS Image Governance Tools
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Scan ShareDrive and sync records into pms_item_image_check, then refresh dashboard">
            <span>
              <Button
                variant="outlined"
                startIcon={
                  s3ScanStatus === 'running'
                    ? <CircularProgress size={14} color="inherit" />
                    : <RefreshIcon />
                }
                onClick={handleS3Scan}
                disabled={s3ScanStatus === 'running' || loading}
                size="small"
              >
                {s3ScanStatus === 'running' ? 'Refreshing…' : loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={`Export current filtered results to Excel (up to 10,000 rows)`}>
            <span>
              <Button
                variant="outlined"
                color="success"
                startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                onClick={handleExport}
                disabled={exportLoading}
                size="small"
              >
                {exportLoading ? 'Exporting…' : 'Export Excel'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Sign out">
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size="small"
            >
              Logout
            </Button>
          </Tooltip>
        </Box>
      </Box>


      {/* S3 scan in-progress banner */}
      {s3ScanStatus === 'running' && (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} color="inherit" />}
          sx={{ mb: 1, py: 0.5 }}
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <span>
              Scanning Photos…
              {s3ScanResult?.phase && ` (${s3ScanResult.phase})`}
            </span>
            {s3ScanResult?.totalGroups > 0 && (
              <Box sx={{ minWidth: 180 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.round((s3ScanResult.processedGroups / s3ScanResult.totalGroups) * 100)}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {s3ScanResult.processedGroups} / {s3ScanResult.totalGroups} groups processed
                </Typography>
              </Box>
            )}
          </Box>
        </Alert>
      )}

      <Divider sx={{ my: 0.5 }} />
      <Box id="items-list-section" />
      {/* <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mb: 1.5 }}>
        Item List
      </Typography> */}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1 }}>
        {/* Top: PMS Item Overview (full width) */}
        {overview && (
          <Paper sx={{ p: 0.75, minWidth: 0 }}>
            {overview ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: 'auto auto', gap: '3.2px' }}>
                {/* Total Items (spans 2 rows) */}
                <Box sx={{ gridRow: '1 / span 2' }}>
                  <StatsCard
                    title="Total Items"
                    value={overview.total_items?.toLocaleString() || '0'}
                    icon={<InventoryIcon sx={{ fontSize: 14 }} />}
                    color="primary"
                    subtitle="All products in catalog"
                    onClick={() => {
                      setHasImageFilter(null);
                      setInStockFilter(null);
                      setPage(0);
                    }}
                  />
                </Box>
                {/* PHOTO: YES (row 1, col 2) */}
                <Box>
                  <StatsCard
                    title="PHOTO: YES"
                    value={overview.items_with_images?.toLocaleString() || '0'}
                    icon={<ImageIcon sx={{ fontSize: 14 }} />}
                    color="success"
                    subtitle={percentOf(overview.items_with_images, overview.total_items)}
                    onClick={() => {
                      setHasImageFilter(true);
                      setInStockFilter(null);
                      setNoPhotoGrOverdueFilter(false);
                      setPage(0);
                    }}
                  />
                </Box>
                {/* PHOTO: NO (row 2, col 2) */}
                <Box>
                  <StatsCard
                    title="PHOTO: NO"
                    value={overview.items_without_images?.toLocaleString() || '0'}
                    icon={<ImageNotSupported sx={{ fontSize: 14 }} />}
                    color="error"
                    subtitle={percentOf(overview.items_without_images, overview.total_items)}
                    onClick={() => {
                      setHasImageFilter(false);
                      setInStockFilter(null);
                      setFrameShapeMismatchFilter(false);
                      setFrameShapeMatchFilter(false);
                      setPhotoNoOrMismatchFilter(false);
                      setPage(0);
                    }}
                  />
                </Box>
                {/* AI SHAPE: MATCHED (hidden) */}
                <Box sx={{ display: 'none' }}>
                  {hasImageFilter !== false ? (
                    <StatsCard
                      title="AI SHAPE: MATCHED"
                      value={overview.items_ai_shape_matched?.toLocaleString() ?? '—'}
                      icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                      color="success"
                      subtitle={percentOf(overview.items_ai_shape_matched, overview.total_items)}
                      onClick={() => {
                        setFrameShapeMatchFilter(true);
                        setFrameShapeMismatchFilter(false);
                        setNoAiFrameShapeFilter(false);
                        setPage(0);
                      }}
                    />
                  ) : (
                    <StatsCard
                      title="NO PHOTO ≥ 30D + STOCK"
                      value={overview.items_no_photo_gr_overdue?.toLocaleString() ?? '—'}
                      icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                      color="warning"
                      subtitle="No photo, GR ≥ 30 days ago, SOH > 0"
                      onClick={() => {
                        setNoPhotoGrOverdueFilter(true);
                        setHasImageFilter(false);
                        setPage(0);
                      }}
                    />
                  )}
                </Box>
                {/* AI SHAPE: MISMATCH (hidden) */}
                <Box sx={{ display: 'none' }}>
                  {hasImageFilter !== false && (
                    <StatsCard
                      title="AI SHAPE: MISMATCH"
                      value={overview.items_ai_shape_mismatch?.toLocaleString() ?? '—'}
                      icon={<CompareArrowsIcon sx={{ fontSize: 14 }} />}
                      color="error"
                      subtitle={percentOf(overview.items_ai_shape_mismatch, overview.total_items)}
                      onClick={() => {
                        setFrameShapeMismatchFilter(true);
                        setNoAiFrameShapeFilter(false);
                        setFrameShapeMatchFilter(false);
                        setPhotoNoOrMismatchFilter(false);
                        setPage(0);
                      }}
                    />
                  )}
                </Box>
                {/* No Photo / Shape Mismatch button (hidden) */}
                <Box sx={{ display: 'none' }}>
                  <Button
                    variant={photoNoOrMismatchFilter ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => {
                      setPhotoNoOrMismatchFilter(true);
                      setHasImageFilter(null);
                      setFrameShapeMismatchFilter(false);
                      setFrameShapeMatchFilter(false);
                      setNoAiFrameShapeFilter(false);
                      setNoPhotoGrOverdueFilter(false);
                      setPage(0);
                    }}
                    size="small"
                    fullWidth
                    sx={{ py: '2px', fontSize: '0.72rem', minHeight: 0, height: '100%' }}
                  >
                    No Photo / Shape Mismatch
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height={60}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        )}

        {/* Bottom: Filters (full width) */}
        <Paper sx={{ p: 0.75, minWidth: 0 }}>
          {/* Row 1 — Primary Filters (hidden — now using inline column filters) */}
          <Box sx={{ display: 'none', gap: 0.5, mb: 0.5, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={brands.map(b => b.brand)}
                value={brandFilter}
                onChange={(event, newValue) => { setBrandFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="Brand" placeholder="Brand..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={seasons.map(s => s.vendor_season)}
                value={seasonFilter}
                onChange={(event, newValue) => { setSeasonFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="Season" placeholder="Season..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={categories.map(c => c.category)}
                value={categoryFilter}
                onChange={(event, newValue) => { setCategoryFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="Category" placeholder="Category..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={frameShapes.map(s => s.frame_shape)}
                value={frameShapeFilter}
                onChange={(event, newValue) => { setFrameShapeFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="Frame Shape" placeholder="Shape..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={frameShapes.map(s => s.frame_shape).filter(s => s !== 'R.W.A.')}
                value={aiFrameShapeFilter}
                onChange={(event, newValue) => { setAiFrameShapeFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="AI Frame Shape" placeholder="AI Shape..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                limitTags={1}
                options={itemModels.map(m => m.item_model)}
                value={itemModelFilter}
                onChange={(event, newValue) => { setItemModelFilter(newValue); setPage(0); }}
                size="small"
                sx={{ '& .MuiInputBase-root': { py: '1px', fontSize: '0.72rem' }, '& .MuiAutocomplete-tag': { my: 0 } }}
                renderInput={(params) => <TextField {...params} label="Item Model" placeholder="Model..." size="small" InputLabelProps={{ sx: { fontSize: '0.72rem' } }} />}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...tagProps } = getTagProps({ index }); return <Chip key={key} label={option} {...tagProps} size="small" />; })}
              />
            </Box>
            <Box sx={{ flex: 1.5, minWidth: 0 }}>
              <TextField
                fullWidth
                placeholder="Search item code, name, brand..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                size="small"
                InputProps={{
                  sx: { fontSize: '0.72rem', py: '1px' },
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '0.9rem' }} /></InputAdornment>
                }}
              />
            </Box>
          </Box>

          {/* Row 2 — Active Filter Chips + Quick Filters (boolean toggles) */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasActiveFilters && (
              <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />} size="small" sx={{ py: '1px', px: 1, fontSize: '0.68rem', minHeight: 0 }}>Clear</Button>
            )}
            {hasActiveFilters && (
              <>
                {searchTerm && (
                  <Chip label={`Search: ${searchTerm}`} size="small" onDelete={() => { setSearchTerm(''); setSearchInput(''); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                {brandFilter.map((brand) => (
                  <Chip key={brand} label={`Brand: ${brand}`} size="small" onDelete={() => { setBrandFilter(brandFilter.filter(b => b !== brand)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {seasonFilter.map((season) => (
                  <Chip key={season} label={`Season: ${season}`} size="small" onDelete={() => { setSeasonFilter(seasonFilter.filter(s => s !== season)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {categoryFilter.map((category) => (
                  <Chip key={category} label={`Category: ${category}`} size="small" onDelete={() => { setCategoryFilter(categoryFilter.filter(c => c !== category)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {frameShapeFilter.map((shape) => (
                  <Chip key={shape} label={`Shape: ${shape}`} size="small" onDelete={() => { setFrameShapeFilter(frameShapeFilter.filter(s => s !== shape)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {aiFrameShapeFilter.map((shape) => (
                  <Chip key={shape} label={`AI Shape: ${shape}`} size="small" onDelete={() => { setAiFrameShapeFilter(aiFrameShapeFilter.filter(s => s !== shape)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {itemModelFilter.map((model) => (
                  <Chip key={model} label={`Model: ${model}`} size="small" onDelete={() => { setItemModelFilter(itemModelFilter.filter(m => m !== model)); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                ))}
                {hasImageFilter !== null && (
                  <Chip label={hasImageFilter ? 'Images: With' : 'Images: Without'} size="small" onDelete={() => { setHasImageFilter(null); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                {inStockFilter !== null && (
                  <Chip label={inStockFilter ? 'SOH: With' : 'SOH: Without'} size="small" onDelete={() => { setInStockFilter(null); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
                {hasPoFilter !== null && (
                  <Chip label={hasPoFilter ? 'PO: With' : 'PO: Without'} size="small" onDelete={() => { setHasPoFilter(null); setPage(0); }} sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
              </>
            )}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
            <Chip label="AI Shape: Pending" size="small" variant={noAiFrameShapeFilter ? 'filled' : 'outlined'} color={noAiFrameShapeFilter ? 'warning' : 'default'} onClick={() => { setNoAiFrameShapeFilter(!noAiFrameShapeFilter); setPage(0); }} sx={{ fontSize: '0.65rem', height: 20 }} />
            <Chip label="Shape: Mismatch" size="small" variant={frameShapeMismatchFilter ? 'filled' : 'outlined'} color={frameShapeMismatchFilter ? 'error' : 'default'} onClick={() => { setFrameShapeMismatchFilter(!frameShapeMismatchFilter); if (!frameShapeMismatchFilter) { setFrameShapeMatchFilter(false); } setPage(0); }} sx={{ fontSize: '0.65rem', height: 20 }} />
            <Chip label="Shape: Matched" size="small" variant={frameShapeMatchFilter ? 'filled' : 'outlined'} color={frameShapeMatchFilter ? 'success' : 'default'} onClick={() => { setFrameShapeMatchFilter(!frameShapeMatchFilter); if (!frameShapeMatchFilter) { setFrameShapeMismatchFilter(false); } setPage(0); }} sx={{ fontSize: '0.65rem', height: 20 }} />
            <Chip label="No Photo+GR≥30d+Stock>0" size="small" variant={noPhotoGrOverdueFilter ? 'filled' : 'outlined'} color={noPhotoGrOverdueFilter ? 'warning' : 'default'} onClick={() => { setNoPhotoGrOverdueFilter(!noPhotoGrOverdueFilter); setPage(0); }} sx={{ fontSize: '0.65rem', height: 20 }} />
            <Chip label="No Photo / Mismatch" size="small" variant={photoNoOrMismatchFilter ? 'filled' : 'outlined'} color={photoNoOrMismatchFilter ? 'error' : 'default'} onClick={() => { setPhotoNoOrMismatchFilter(!photoNoOrMismatchFilter); setPage(0); }} sx={{ fontSize: '0.65rem', height: 20 }} />
          </Box>
        </Paper>
      </Box>

      {itemsError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {itemsError}
        </Alert>
      )}

      <Paper sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <Table
            size="small"
            stickyHeader
            sx={{
              '& .MuiTableCell-root': {
                py: '2px',
                px: '4px',
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
                borderBottom: '1px solid rgba(224,224,224,0.8)',
              },
              '& .MuiTableSortLabel-root': { fontSize: '0.8rem' },
              '& .MuiTableSortLabel-icon': { fontSize: '0.6rem !important', width: 12, height: 12 },
              '& .MuiChip-root': { height: 16, fontSize: '0.6rem' },
              '& .MuiChip-label': { px: '4px'},
            }}
          >
            <TableHead>
              <TableRow ref={titleRowRef} sx={{ backgroundColor: 'primary.light', '& .MuiTableCell-root': { top: 0 } }}>
                <TableCell sx={{ width: 88, minWidth: 88, maxWidth: 88, px: '2px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 0, zIndex: 4, backgroundColor: 'primary.light' }}>
                  <TableSortLabel
                    active={sortBy === 'item_code'}
                    direction={sortBy === 'item_code' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('item_code')}
                  >
                    <strong>Item Code</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 92, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '2px 0 4px rgba(0,0,0,0.12)' }}>
                  <TableSortLabel
                    active={sortBy === 'brand'}
                    direction={sortBy === 'brand' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('brand')}
                  >
                    <strong>Brand</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 192, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '2px 0 4px rgba(0,0,0,0.12)' }}>
                  <TableSortLabel
                    active={sortBy === 'item_model'}
                    direction={sortBy === 'item_model' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('item_model')}
                  >
                    <strong>Item Model</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: 80, minWidth: 80, maxWidth: 80, px: '4px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 292, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '2px 0 4px rgba(0,0,0,0.12)' }}>
                  <TableSortLabel
                    active={sortBy === 'color_code'}
                    direction={sortBy === 'color_code' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('color_code')}
                  >
                    <strong>Color Code</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: 52, minWidth: 52, maxWidth: 52, borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 372, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '2px 0 4px rgba(0,0,0,0.12)' }}><strong>Preview</strong></TableCell>
                <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 424, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '2px 0 4px rgba(0,0,0,0.12)' }}>
                  <TableSortLabel
                    active={sortBy === 'image_count'}
                    direction={sortBy === 'image_count' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('image_count')}
                  >
                    <strong>Images</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'item_name'}
                    direction={sortBy === 'item_name' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('item_name')}
                  >
                    <strong>Item Name</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'category'}
                    direction={sortBy === 'category' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    <strong>Category</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'collection'}
                    direction={sortBy === 'collection' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('collection')}
                  >
                    <strong>Collection</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'vendor_season'}
                    direction={sortBy === 'vendor_season' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('vendor_season')}
                  >
                    <strong>Season</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'frame_shape'}
                    direction={sortBy === 'frame_shape' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('frame_shape')}
                  >
                    <strong>Frame Shape</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'ai_frame_shape'}
                    direction={sortBy === 'ai_frame_shape' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('ai_frame_shape')}
                  >
                    <strong>AI Frame Shape</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'created_date'}
                    direction={sortBy === 'created_date' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('created_date')}
                  >
                    <strong>Created Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'hk_last_bo_date'}
                    direction={sortBy === 'hk_last_bo_date' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('hk_last_bo_date')}
                  >
                    <strong>Last BO Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'hk_bo_ord_qty'}
                    direction={sortBy === 'hk_bo_ord_qty' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('hk_bo_ord_qty')}
                  >
                    <strong>Total BO Qty</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'first_gr_date'}
                    direction={sortBy === 'first_gr_date' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('first_gr_date')}
                  >
                    <strong>HK 1st GR Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'gr_total_qty'}
                    direction={sortBy === 'gr_total_qty' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('gr_total_qty')}
                  >
                    <strong>HK GR Total Qty</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'last_trx_date'}
                    direction={sortBy === 'last_trx_date' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('last_trx_date')}
                  >
                    <strong>HK Last Trx Date</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'total_sales_item_qty'}
                    direction={sortBy === 'total_sales_item_qty' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('total_sales_item_qty')}
                  >
                    <strong>HK Total Sales Item Qty</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'stock'}
                    direction={sortBy === 'stock' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('stock')}
                  >
                    <strong>HK Stock</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                  <TableSortLabel
                    active={sortBy === 'hold_qty'}
                    direction={sortBy === 'hold_qty' ? sortOrder.toLowerCase() : 'asc'}
                    onClick={() => handleSort('hold_qty')}
                  >
                    <strong>HK Hold Qty</strong>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ position: 'sticky', right: 0, zIndex: 4, backgroundColor: 'primary.light', boxShadow: '-2px 0 4px rgba(0,0,0,0.12)' }}><strong>Actions</strong></TableCell>
              </TableRow>
              {/* Inline Column Filter Row */}
              <TableRow sx={{ '& .MuiTableCell-root': { py: '2px', px: '2px', backgroundColor: '#f5f7fa', borderBottom: '2px solid rgba(224,224,224,1)', top: titleRowHeight ? `${titleRowHeight}px` : '29px' } }}>
                {/* Item Code — text search */}
                <TableCell sx={{ width: 88, minWidth: 88, maxWidth: 88, position: 'sticky', left: 0, zIndex: 4, backgroundColor: '#f5f7fa' }}>
                  <TextField
                    fullWidth size="small" placeholder="Code..."
                    value={colFilterItemCode}
                    onChange={(e) => setColFilterItemCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSearchInput(colFilterItemCode); setSearchTerm(colFilterItemCode); setPage(0); } }}
                    InputProps={{ sx: { fontSize: '0.65rem', py: 0, height: 24 } }}
                  />
                </TableCell>
                {/* Brand — dropdown (synced with left panel) */}
                <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', position: 'sticky', left: 92, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                  <Autocomplete
                    multiple limitTags={0}
                    options={brands.map(b => b.brand)}
                    value={brandFilter}
                    onChange={(e, v) => { setBrandFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="Brand..." size="small" />}
                  />
                </TableCell>
                {/* Item Model — dropdown (synced with left panel) */}
                <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', position: 'sticky', left: 192, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                  <Autocomplete
                    multiple limitTags={0}
                    options={itemModels.map(m => m.item_model)}
                    value={itemModelFilter}
                    onChange={(e, v) => { setItemModelFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="Model..." size="small" />}
                  />
                </TableCell>
                {/* Color Code — text search */}
                <TableCell sx={{ width: 80, minWidth: 80, maxWidth: 80, px: '4px', position: 'sticky', left: 292, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                  <TextField
                    fullWidth size="small" placeholder="Color..."
                    value={colFilterColorCode}
                    onChange={(e) => setColFilterColorCode(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSearchInput(colFilterColorCode); setSearchTerm(colFilterColorCode); setPage(0); } }}
                    InputProps={{ sx: { fontSize: '0.65rem', py: 0, height: 24 } }}
                  />
                </TableCell>
                {/* Preview — no filter */}
                <TableCell sx={{ width: 52, minWidth: 52, maxWidth: 52, position: 'sticky', left: 372, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }} />
                {/* Images — no filter */}
                <TableCell sx={{ width: 70, minWidth: 70, maxWidth: 70, position: 'sticky', left: 424, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }} />
                {/* Item Name — text search */}
                <TableCell>
                  <TextField
                    fullWidth size="small" placeholder="Name..."
                    value={colFilterItemName}
                    onChange={(e) => setColFilterItemName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSearchInput(colFilterItemName); setSearchTerm(colFilterItemName); setPage(0); } }}
                    InputProps={{ sx: { fontSize: '0.65rem', py: 0, height: 24 } }}
                  />
                </TableCell>
                {/* Category — dropdown (synced with left panel) */}
                <TableCell>
                  <Autocomplete
                    multiple limitTags={0}
                    options={categories.map(c => c.category)}
                    value={categoryFilter}
                    onChange={(e, v) => { setCategoryFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="Category..." size="small" />}
                  />
                </TableCell>
                {/* Collection — text search */}
                <TableCell>
                  <TextField
                    fullWidth size="small" placeholder="Collection..."
                    value={colFilterCollection}
                    onChange={(e) => setColFilterCollection(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setSearchInput(colFilterCollection); setSearchTerm(colFilterCollection); setPage(0); } }}
                    InputProps={{ sx: { fontSize: '0.65rem', py: 0, height: 24 } }}
                  />
                </TableCell>
                {/* Season — dropdown (synced with left panel) */}
                <TableCell>
                  <Autocomplete
                    multiple limitTags={0}
                    options={seasons.map(s => s.vendor_season)}
                    value={seasonFilter}
                    onChange={(e, v) => { setSeasonFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="Season..." size="small" />}
                  />
                </TableCell>
                {/* Frame Shape — dropdown (synced with left panel) */}
                <TableCell>
                  <Autocomplete
                    multiple limitTags={0}
                    options={frameShapes.map(s => s.frame_shape)}
                    value={frameShapeFilter}
                    onChange={(e, v) => { setFrameShapeFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="Shape..." size="small" />}
                  />
                </TableCell>
                {/* AI Frame Shape — dropdown (synced with left panel) */}
                <TableCell>
                  <Autocomplete
                    multiple limitTags={0}
                    options={frameShapes.map(s => s.frame_shape).filter(s => s !== 'R.W.A.')}
                    value={aiFrameShapeFilter}
                    onChange={(e, v) => { setAiFrameShapeFilter(v); setPage(0); }}
                    size="small" disableCloseOnSelect
                    sx={{ minWidth: 60, '& .MuiInputBase-root': { py: 0, fontSize: '0.65rem', height: 24, flexWrap: 'nowrap' } }}
                    renderTags={(value) => <span style={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block', maxWidth: '100%' }}>{value.join(', ')}</span>}
                    renderInput={(params) => <TextField {...params} placeholder="AI Shape..." size="small" />}
                  />
                </TableCell>
                {/* Created Date — empty */}
                <TableCell />
                {/* Last BO Date — empty */}
                <TableCell />
                {/* Total BO Qty — empty */}
                <TableCell />
                {/* 1st GR Date — empty */}
                <TableCell />
                {/* GR Total Qty — empty */}
                <TableCell />
                {/* Last Trx Date — empty */}
                <TableCell />
                {/* Total Sales Item Qty — empty */}
                <TableCell />
                {/* Stock — empty */}
                <TableCell />
                {/* Hold Qty — empty */}
                <TableCell />
                {/* Actions — no filter */}
                <TableCell sx={{ position: 'sticky', right: 0, zIndex: 4, backgroundColor: '#f5f7fa', boxShadow: '-2px 0 4px rgba(0,0,0,0.08)' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsLoading ? (
                <TableRow>
                  <TableCell colSpan={30} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={30} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No items found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.item_code}
                    hover
                  >
                    <TableCell sx={{ width: 92, minWidth: 92, maxWidth: 92, px: '2px', position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'background.paper' }}>{item.item_code}</TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: 92, zIndex: 2, backgroundColor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>{item.brand || 'N/A'}</TableCell>
                    <TableCell sx={{ width: 100, minWidth: 100, maxWidth: 100, px: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: 192, zIndex: 2, backgroundColor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>{item.item_model || '—'}</TableCell>
                    <TableCell sx={{ width: 80, minWidth: 80, maxWidth: 80, px: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: 292, zIndex: 2, backgroundColor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                      <Tooltip
                        title={
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, p: 0.5 }}>
                            <strong style={{ gridColumn: 'span 2' }}>Colors</strong>
                            <span style={{ fontSize: '0.85em' }}>Code:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.color_code || '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>Detail:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.color_detail || '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>Front:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.color_front || '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>Temple:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.color_temple || '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>Lens:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.color_lens || '—'}</span>
                          </Box>
                        }
                        placement="right"
                        arrow
                      >
                        <span style={{ cursor: 'pointer' }}>{item.color_code || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 52, minWidth: 52, maxWidth: 52, p: '2px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 372, zIndex: 2, backgroundColor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                      {item.thumbnail_url ? (
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <img
                                src={item.thumbnail_url}
                                alt={item.item_code}
                                style={{ width: 220, height: 220, objectFit: 'contain', display: 'block' }}
                              />
                            </Box>
                          }
                          placement="right"
                          arrow
                        >
                          <img
                            src={item.thumbnail_url}
                            alt={item.item_code}
                            style={{ width: 40, height: 40, objectFit: 'contain', display: 'block', cursor: 'pointer', borderRadius: 4, margin: '0 auto' }}
                            onClick={() => handleViewItem(item.item_code)}
                          />
                        </Tooltip>
                      ) : (
                        <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', borderRadius: 1, margin: '0 auto' }}>
                          <ImageNotSupported sx={{ fontSize: 16, color: 'grey.400' }} />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ width: 70, minWidth: 70, maxWidth: 70, p: '2px', borderRight: '1px solid rgba(224, 224, 224, 1)', position: 'sticky', left: 424, zIndex: 2, backgroundColor: 'background.paper', boxShadow: '2px 0 4px rgba(0,0,0,0.08)' }}>
                      <Chip
                        label={item.image_count || 0}
                        size="small"
                        color={item.image_count > 0 ? "success" : "default"}
                        variant={item.image_count > 0 ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      <Tooltip title={item.item_name || ''} placement="top" arrow>
                        <span>{item.item_name || 'N/A'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{item.category || 'N/A'}</TableCell>
                    <TableCell sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.collection || 'N/A'}</TableCell>
                    <TableCell>{item.vendor_season || 'N/A'}</TableCell>
                    <TableCell>{item.frame_shape || '—'}</TableCell>
                    <TableCell sx={
                      item.ai_frame_shape && item.frame_shape && item.ai_frame_shape !== item.frame_shape
                        ? { color: 'error.main', fontWeight: 'bold', bgcolor: 'error.50' }
                        : undefined
                    }>{item.ai_frame_shape || '—'}</TableCell>
                    <TableCell>{formatDate(item.created_date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip
                        title={
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, p: 0.5 }}>
                            <strong style={{ gridColumn: 'span 2' }}>Last BO Date by Region</strong>
                            <span style={{ fontSize: '0.85em' }}>HK:</span>
                            <span style={{ fontSize: '0.85em' }}>{formatDate(item.hk_last_bo_date)}</span>
                            <span style={{ fontSize: '0.85em' }}>CN:</span>
                            <span style={{ fontSize: '0.85em' }}>{formatDate(item.cn_last_bo_date)}</span>
                            <span style={{ fontSize: '0.85em' }}>SG:</span>
                            <span style={{ fontSize: '0.85em' }}>{formatDate(item.sg_last_bo_date)}</span>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <span style={{ cursor: 'pointer' }}>
                          {(() => {
                            const dates = [item.hk_last_bo_date, item.cn_last_bo_date, item.sg_last_bo_date].filter(Boolean);
                            if (dates.length === 0) return 'N/A';
                            return formatDate(dates.reduce((a, b) => new Date(a) > new Date(b) ? a : b));
                          })()}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 0.5, p: 0.5 }}>
                            <strong style={{ gridColumn: 'span 2' }}>BO Qty by Region</strong>
                            <span style={{ fontSize: '0.85em' }}>HK:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.hk_bo_ord_qty != null ? Number(item.hk_bo_ord_qty).toLocaleString() : '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>CN:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.cn_bo_ord_qty != null ? Number(item.cn_bo_ord_qty).toLocaleString() : '—'}</span>
                            <span style={{ fontSize: '0.85em' }}>SG:</span>
                            <span style={{ fontSize: '0.85em' }}>{item.sg_bo_ord_qty != null ? Number(item.sg_bo_ord_qty).toLocaleString() : '—'}</span>
                          </Box>
                        }
                        placement="top"
                        arrow
                      >
                        <span style={{ cursor: 'pointer' }}>
                          {(() => {
                            const total = (Number(item.hk_bo_ord_qty) || 0) + (Number(item.cn_bo_ord_qty) || 0) + (Number(item.sg_bo_ord_qty) || 0);
                            const hasAny = item.hk_bo_ord_qty != null || item.cn_bo_ord_qty != null || item.sg_bo_ord_qty != null;
                            return hasAny ? total.toLocaleString() : '—';
                          })()}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.first_gr_date)}</TableCell>
                    <TableCell align="right">{item.gr_total_qty != null ? Number(item.gr_total_qty).toLocaleString() : '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(item.last_trx_date)}</TableCell>
                    <TableCell align="right">{item.total_sales_item_qty != null ? Number(item.total_sales_item_qty).toLocaleString() : '—'}</TableCell>
                    <TableCell align="right">{item.stock != null ? item.stock : '—'}</TableCell>
                    <TableCell align="right">{item.bo_hold_qty != null ? item.bo_hold_qty : '—'}</TableCell>
                    <TableCell align="center" sx={{ position: 'sticky', right: 0, zIndex: 1, backgroundColor: 'background.paper', boxShadow: '-2px 0 4px rgba(0,0,0,0.08)', whiteSpace: 'nowrap' }}>
                      <Tooltip title="View item">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewItem(item.item_code)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {item.brand && item.item_code ? (
                        <Tooltip title="Upload photo">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => handleOpenUploadModal(e, item)}
                          >
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Cannot upload: missing Brand or Item Code">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled
                            >
                              <UploadIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* S3 scan result Snackbar */}
      <ItemUploadModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        item={selectedItemForUpload}
        onUploadSuccess={handleUploadSuccess}
      />

      <Snackbar
        open={uploadSnackOpen}
        autoHideDuration={5000}
        onClose={() => setUploadSnackOpen(false)}
        message={uploadSnackMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Snackbar
        open={s3ScanSnackOpen}
        autoHideDuration={8000}
        onClose={() => setS3ScanSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setS3ScanSnackOpen(false)}
          severity={s3ScanStatus === 'completed' ? 'success' : 'error'}
          variant="filled"
          sx={{ width: '100%', minWidth: 320 }}
        >
          {s3ScanStatus === 'completed' && s3ScanResult ? (
            <>
              <strong>Sync photos complete</strong><br />
              {s3ScanResult.totalImageObjects?.toLocaleString()} images scanned ·{' '}
              +{s3ScanResult.added} added ·{' '}
              {s3ScanResult.updated} updated ·{' '}
              {s3ScanResult.deleted} deleted
            </>
          ) : (
            <><strong>Sync photos failed</strong><br />{s3ScanError}</>
          )}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Dashboard;
